import type { BTCWalletProvider } from "@/lib/services/btc-wallet-providers";

export type BitcoinAddressType =
	| "taproot"
	| "segwit"
	| "nested-segwit"
	| "legacy"
	| "unknown";

export type WalletSignatureProtocol = "ecdsa" | "bip322-simple";

export interface BitcoinSigningProfile {
	addressType: BitcoinAddressType;
	primaryProtocol: WalletSignatureProtocol;
	fallbackProtocols: WalletSignatureProtocol[];
}

const SIGN_REJECTION_PATTERNS = [
	"user rejected",
	"user denied",
	"cancelled",
	"canceled",
	"rejected",
];

const RETRYABLE_SIGN_PATTERNS = [
	"invalid parameters",
	"legal address",
	"unsupported",
	"not support",
	"not implemented",
	"invalid param",
];

export function resolveBitcoinAddressType(address: string): BitcoinAddressType {
	if (address.startsWith("bc1p")) return "taproot";
	if (address.startsWith("bc1q")) return "segwit";
	if (address.startsWith("3")) return "nested-segwit";
	if (address.startsWith("1")) return "legacy";
	return "unknown";
}

export function resolveBitcoinSigningProfile(
	walletType: string,
	address: string,
): BitcoinSigningProfile {
	const normalizedWalletType = walletType.toLowerCase();
	const addressType = resolveBitcoinAddressType(address);

	if (normalizedWalletType === "phantom") {
		return {
			addressType,
			primaryProtocol: "bip322-simple",
			fallbackProtocols: [],
		};
	}

	if (normalizedWalletType === "xverse") {
		return {
			addressType,
			primaryProtocol: addressType === "taproot" ? "bip322-simple" : "ecdsa",
			fallbackProtocols: [],
		};
	}

	if (normalizedWalletType === "okx") {
		return {
			addressType,
			primaryProtocol: "ecdsa",
			fallbackProtocols: [],
		};
	}

	if (normalizedWalletType === "tokenpocket") {
		return {
			addressType,
			primaryProtocol: addressType === "taproot" ? "bip322-simple" : "ecdsa",
			fallbackProtocols: [],
		};
	}

	if (["unisat", "wizz", "binance"].includes(normalizedWalletType)) {
		return {
			addressType,
			primaryProtocol: "ecdsa",
			fallbackProtocols: [],
		};
	}

	return {
		addressType,
		primaryProtocol:
			addressType === "taproot" || addressType === "nested-segwit"
				? "bip322-simple"
				: "ecdsa",
		fallbackProtocols: [],
	};
}

export function buildSiwbSignMessageType(protocol: WalletSignatureProtocol): {
	ECDSA: null;
} | {
	Bip322Simple: null;
} {
	return protocol === "bip322-simple" ? { Bip322Simple: null } : { ECDSA: null };
}

export function normalizeBitcoinPublicKey(
	address: string,
	publicKey?: string,
): string | undefined {
	if (!publicKey) {
		return publicKey;
	}

	if (address.startsWith("bc1p")) {
		if (
			publicKey.length === 66 &&
			(publicKey.startsWith("02") || publicKey.startsWith("03"))
		) {
			return publicKey.slice(2);
		}

		return publicKey;
	}

	if (
		publicKey.length === 64 &&
		!publicKey.startsWith("02") &&
		!publicKey.startsWith("03")
	) {
		return `02${publicKey}`;
	}

	return publicKey;
}

function getErrorMessage(error: unknown): string {
	if (error instanceof Error) {
		return error.message;
	}

	if (typeof error === "string") {
		return error;
	}

	try {
		return JSON.stringify(error);
	} catch {
		return String(error);
	}
}

function isUserRejectedError(message: string): boolean {
	const normalizedMessage = message.toLowerCase();
	return SIGN_REJECTION_PATTERNS.some((pattern) =>
		normalizedMessage.includes(pattern),
	);
}

function isRetryableSignError(message: string): boolean {
	const normalizedMessage = message.toLowerCase();
	return RETRYABLE_SIGN_PATTERNS.some((pattern) =>
		normalizedMessage.includes(pattern),
	);
}

export async function signBitcoinMessageWithFallback(params: {
	wallet: BTCWalletProvider;
	walletType: string;
	address: string;
	message: string;
	log?: (level: "info" | "warn", message: string, data?: unknown) => void;
}): Promise<{
	signature: string;
	protocol: WalletSignatureProtocol;
	addressType: BitcoinAddressType;
}> {
	const { wallet, walletType, address, message, log } = params;
	const profile = resolveBitcoinSigningProfile(walletType, address);
	const protocols = [profile.primaryProtocol, ...profile.fallbackProtocols];
	let lastError: unknown;

	for (let index = 0; index < protocols.length; index += 1) {
		const protocol = protocols[index];

		try {
			log?.("info", "Attempting BTC message signature", {
				walletType,
				addressType: profile.addressType,
				protocol,
				attempt: index + 1,
				totalAttempts: protocols.length,
			});

			const signature = await wallet.signMessage(message, address, {
				signatureType: protocol,
				addressType: profile.addressType,
			});

			return {
				signature,
				protocol,
				addressType: profile.addressType,
			};
		} catch (error) {
			lastError = error;
			const errorMessage = getErrorMessage(error);
			const hasFallback = index < protocols.length - 1;
			const canRetry =
				hasFallback &&
				!isUserRejectedError(errorMessage) &&
				isRetryableSignError(errorMessage);

			log?.(
				canRetry ? "warn" : "info",
				canRetry
					? "BTC message signing failed, retrying with fallback protocol"
					: "BTC message signing failed",
				{
					walletType,
					addressType: profile.addressType,
					protocol,
					error: errorMessage,
					canRetry,
				},
			);

			if (!canRetry) {
				throw error;
			}
		}
	}

	throw lastError instanceof Error
		? lastError
		: new Error(getErrorMessage(lastError));
}
